import { getAllUsers } from '@dealstack/frontend/shared/dataExchange/services/god.service';
import { useDebounceValue } from '@dealstack/frontend/shared/other/react/hooks/useDebounceValue';
import { asyncLoadExceljs } from '@dealstack/frontend/shared/utils/asyncLoadExceljs';
import { EXCEL_SPREADSHEET_TYPE } from '@dealstack/shared/lib/common/business/excel';
import { DataUserTct } from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useQuery } from '@tanstack/react-query';
import {
	getCoreRowModel,
	getSortedRowModel,
	SortingState,
	useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { saveAs } from 'file-saver';
import { useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { manageInvestorsColumns } from './ListUser';

export enum MANAGE_USER_FORM_FIELDS {
	SEARCH_QUERY = 'search_query',
	RESULT_LIMIT = 'result_limit',
}

const EMPTY_DATA: Array<DataUserTct> = [];

export const useListUser = () => {
	const manageInvestorsForm = useForm({
		mode: 'all',
		reValidateMode: 'onChange',
	});
	const [sorting, setSorting] = useState<SortingState>([]);

	const emailValue = useWatch({
		control: manageInvestorsForm.control,
		name: MANAGE_USER_FORM_FIELDS.SEARCH_QUERY,
	});
	const searchLimit = useWatch({
		control: manageInvestorsForm.control,
		name: MANAGE_USER_FORM_FIELDS.RESULT_LIMIT,
	});

	const searchQuery = useDebounceValue(emailValue, 500);

	const getUsersRequest = useQuery({
		queryKey: [
			API_PATHS.GODMODE_USERS_ALL_GET,
			{
				searchQuery,
				searchLimit,
			},
		],
		queryFn: () =>
			getAllUsers(API_PATHS.GODMODE_USERS_ALL_GET, {
				searchQuery,
				searchLimit,
			}),
		meta: { errorToast: 'Data cannot be shown!' },
	});

	const tanstackTable = useReactTable({
		data: getUsersRequest.data?.payload ?? EMPTY_DATA,
		columns: manageInvestorsColumns(),
		onSortingChange: setSorting,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		state: {
			sorting,
		},
	});
	const tableContainerRef = useRef<HTMLDivElement>(null);
	const { rows } = tanstackTable.getRowModel();
	const rowVirtualizer = useVirtualizer({
		getScrollElement: () => tableContainerRef.current,
		count: rows.length,
		estimateSize: () => 50,
		overscan: 10,
	});

	const downloadXLSX = async () => {
		const ExcelJS = await asyncLoadExceljs();
		const workbook = new ExcelJS.Workbook();
		workbook.created = new Date();
		const dataWorksheet = workbook.addWorksheet(`bulkEdit`);
		/**
		 * d.slaby 04.05.2023 CORE-115
		 * We take first user from the list so we can pull Array of keys from object so we can
		 * create Columns that match those fields
		 */
		const firstUserEntryObject = getUsersRequest.data?.payload?.[0];
		if (!firstUserEntryObject) {
			return null;
		}
		dataWorksheet.columns = [...Object.keys(firstUserEntryObject)]
			// Allow only specific keys (subset) to be displayed.
			.filter((key) => ['id', 'email', 'firstName', 'lastName'].includes(key))
			.map((name) => ({
				header: name,
				key: name,
				width: 20,
			}));
		dataWorksheet.addRows(
			getUsersRequest.data?.payload?.map((item) => ({
				...item,
			})) ?? []
		);
		const xls64 = await workbook.xlsx.writeBuffer();
		saveAs(
			new Blob([xls64], {
				type: EXCEL_SPREADSHEET_TYPE,
			}),
			`support_app_list_user_${searchQuery ?? ''}-${new Date().toISOString()}.xlsx`
		);
	};

	return {
		downloadXLSX,
		getUsersRequest,
		rows,
		tableContainerRef,
		rowVirtualizer,
		tanstackTable,
		manageInvestorsForm,
	};
};
